import { Box } from 'lucide-react';
import { Suspense, useMemo, useState, useEffect } from 'react';
import { useSubmissionStore } from '@/stores/submissionStore';
import { useTranslation } from '@/lib/i18n';
import { RampScene, aggregateBOM, useRampScene } from '@gorampe/viz-3d';
import type { BOMLeaf, BOMNode } from '@gorampe/viz-3d';
import { SectionCard } from './SectionCard';
import { Alert } from '../ui/Alert';
import { BOMTree } from './BOMTree';
import { PartTooltip } from './PartTooltip';
import { CatalogPanel } from '@/features/catalog';

function formatMoney(n: number, lang: 'fr' | 'en'): string {
  return new Intl.NumberFormat(lang === 'fr' ? 'fr-CA' : 'en-CA', {
    style: 'currency',
    currency: 'CAD',
  }).format(n);
}

function flattenBOM(tree: BOMNode[]): Map<string, BOMLeaf> {
  const out = new Map<string, BOMLeaf>();
  const walk = (n: BOMNode): void => {
    if (n.kind === 'leaf') out.set(n.refId, n);
    else n.children.forEach(walk);
  };
  tree.forEach(walk);
  return out;
}

export function Section04Recap() {
  const submission = useSubmissionStore((s) => s.submission);
  const quote = useSubmissionStore((s) => s.quote);
  const catalog = useSubmissionStore((s) => s.catalog);
  const { t, lang } = useTranslation();
  const layout = useRampScene(submission.config);
  const bomTree = useMemo(() => aggregateBOM(layout, catalog ?? undefined), [layout, catalog]);
  const bomFlat = useMemo(() => flattenBOM(bomTree), [bomTree]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 768px)');
    setIsMobile(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  const bomPanel = (
    <div className="overflow-auto" style={{ maxHeight: 480 }}>
      <strong className="block mb-2 text-xs">{t('bom.title')}</strong>
      {bomTree.length === 0 ? (
        <div className="opacity-60 text-xs">{t('bom.empty')}</div>
      ) : (
        <BOMTree tree={bomTree} />
      )}
    </div>
  );

  return (
    <SectionCard num="04" title={t('section.04.title')} icon={<Box size={18} strokeWidth={2} />}>
      <div className="mb-3" style={{ minHeight: 360 }}>
        <div
          aria-label={t('recap.3d_panel.aria')}
          className="rounded-xl overflow-hidden relative"
          style={{
            background: 'linear-gradient(135deg, var(--brand-navy,#0A1128) 0%, #1E3A5F 100%)',
            height: 360,
            border: '1px solid rgba(34,211,238,0.12)',
          }}
        >
          <Suspense
            fallback={
              <div className="w-full h-full flex items-center justify-center text-cyan-300 text-xs">
                {t('recap.preview3d')}
              </div>
            }
          >
            <RampScene config={submission.config} lowQuality={false} />
          </Suspense>
          {!isMobile && <PartTooltip bomFlat={bomFlat} variant="desktop" />}
        </div>
      </div>

      <CatalogPanel bomContent={bomPanel} bomLabel={t('bom.title')} />

      {isMobile && <PartTooltip bomFlat={bomFlat} variant="mobile" />}

      {quote?.errors && quote.errors.length > 0 && (
        <Alert kind="err">
          <strong className="block mb-1">{t('recap.errors.title')}</strong>
          <ul className="list-disc list-inside space-y-0.5">
            {quote.errors.map((e, i) => (
              <li key={i}>{e.humanMessage}</li>
            ))}
          </ul>
        </Alert>
      )}
      {quote?.warnings && quote.warnings.filter((w) => w.severity === 'warn').length > 0 && (
        <Alert kind="warn">
          <strong className="block mb-1">{t('recap.alerts.title')}</strong>
          <ul className="list-disc list-inside space-y-0.5">
            {quote.warnings
              .filter((w) => w.severity === 'warn')
              .map((w, i) => (
                <li key={i}>{w.humanMessage}</li>
              ))}
          </ul>
        </Alert>
      )}
      {quote?.warnings && quote.warnings.filter((w) => w.severity === 'info').length > 0 && (
        <Alert kind="info">
          <strong className="block mb-1">{t('recap.info.title')}</strong>
          <ul className="list-disc list-inside space-y-0.5">
            {quote.warnings
              .filter((w) => w.severity === 'info')
              .map((w, i) => (
                <li key={i}>{w.humanMessage}</li>
              ))}
          </ul>
        </Alert>
      )}

      {quote && (
        <>
          <div className="mt-3 mb-2">
            {quote.sections.map((s, i) => (
              <div
                key={i}
                className={`flex justify-between items-baseline py-1.5 text-xs ${
                  i < quote.sections.length - 1
                    ? 'border-b border-dashed border-[var(--line,rgba(15,30,51,0.08))]'
                    : ''
                }`}
              >
                <span className="text-[var(--t2,rgba(15,30,51,0.66))]">
                  {s.label}
                  {s.qty > 1 && (
                    <span className="ml-1 text-[var(--t3,rgba(15,30,51,0.42))]">×{s.qty}</span>
                  )}
                </span>
                <strong className="font-mono tabular-nums text-[var(--ink,#0F1E33)] text-[11.5px]">
                  {formatMoney(s.total, lang)}
                </strong>
              </div>
            ))}
          </div>
          <dl className="mt-4 mb-3 grid grid-cols-2 gap-2 rounded-lg bg-slate-50 p-3 text-sm sm:grid-cols-4">
            <div>
              <dt className="text-xs text-slate-500">Forme</dt>
              <dd className="font-medium text-slate-900">{quote.totals.shapeLabel ?? '—'}</dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Longueur totale</dt>
              <dd className="font-medium text-slate-900">
                {quote.totals.runMeters != null && quote.totals.runMeters > 0
                  ? `${quote.totals.runMeters.toFixed(2)} m`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Pente</dt>
              <dd className="font-medium text-slate-900">
                {quote.totals.slopeRatio != null
                  ? `1:${quote.totals.slopeRatio.toFixed(1)} (${quote.totals.slopePercent?.toFixed(1)}%)`
                  : '—'}
              </dd>
            </div>
            <div>
              <dt className="text-xs text-slate-500">Poids total</dt>
              <dd className="font-medium text-slate-900">
                {quote.totals.totalWeightLbs != null && quote.totals.totalWeightLbs > 0
                  ? `${quote.totals.totalWeightLbs.toFixed(0)} lbs (${quote.totals.totalWeightKg?.toFixed(0)} kg)`
                  : '—'}
              </dd>
            </div>
          </dl>
          <div className="bg-[var(--canvas,#F5F7FA)] border border-[var(--line,rgba(15,30,51,0.08))] rounded-xl p-3 text-xs">
            <div className="flex justify-between mb-1">
              <span>{t('recap.subtotal')}</span>
              <span className="font-mono">{formatMoney(quote.totals.subtotal, lang)}</span>
            </div>
            <div className="flex justify-between mb-1">
              <span>{t('recap.tps')}</span>
              <span className="font-mono">{formatMoney(quote.totals.tps, lang)}</span>
            </div>
            <div className="flex justify-between pb-2 mb-2 border-b-2 border-[var(--brand-navy,#0A1128)]">
              <span>{t('recap.tvq')}</span>
              <span className="font-mono">{formatMoney(quote.totals.tvq, lang)}</span>
            </div>
            <div className="flex justify-between font-bold text-sm">
              <span>{t('recap.grand_total')}</span>
              <span className="font-mono" style={{ color: 'var(--brand-primary,#22D3EE)' }}>
                {formatMoney(quote.totals.grandTotal, lang)}
              </span>
            </div>
          </div>
        </>
      )}
    </SectionCard>
  );
}
